import api from './axiosInstance';
import { fileService } from './fileService';

export interface ProfileUpdateData {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  bio?: string;
  profileImage?: string;
  socialLinks?: {
    facebook?: string;
    twitter?: string;
    linkedin?: string;
    instagram?: string;
  };
  address?: {
    country?: string;
    city?: string;
    state?: string;
    street?: string;
    apartment?: string;
    postalCode?: string;
    taxId?: string;
    emergencyNumber?: string;
  };
}

export interface FileUploadResponse {
  success: boolean;
  message: string;
  data?: {
    key: string;
    url: string;
  };
}

export const profileService = {
  /**
   * Upload profile image to S3 (returns object key only).
   */
  uploadProfileImage: async (file: File): Promise<FileUploadResponse> => {
    try {
      const uploaded = await fileService.uploadFile(file);
      return {
        success: true,
        message: 'Image uploaded successfully',
        data: { key: uploaded.key, url: uploaded.url },
      };
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      console.error('Profile image upload error:', error);
      return {
        success: false,
        message: err.response?.data?.message || err.message || 'Failed to upload image',
      };
    }
  },

  /**
   * Update user profile
   */
  updateProfile: async (profileData: ProfileUpdateData): Promise<{ success: boolean; message: string; data?: unknown }> => {
    try {
      const response = await api.patch('/users/profile', profileData);

      return {
        success: true,
        message: 'Profile updated successfully',
        data: response.data.data,
      };
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      console.error('Profile update error:', error);
      return {
        success: false,
        message: err.response?.data?.message || 'Failed to update profile',
      };
    }
  },

  /**
   * Update user profile image: pass S3 object key (or legacy https URL).
   */
  updateProfileImage: async (profileImageKeyOrUrl: string): Promise<{ success: boolean; message: string; data?: unknown }> => {
    try {
      const response = await api.patch('/users/profile/image', {
        profileImage: profileImageKeyOrUrl,
      });

      return {
        success: true,
        message: 'Profile image updated successfully',
        data: response.data.data,
      };
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      console.error('Profile image update error:', error);
      return {
        success: false,
        message: err.response?.data?.message || 'Failed to update profile image',
      };
    }
  },

  /**
   * Get user profile
   */
  getProfile: async (): Promise<{ success: boolean; message: string; data?: unknown }> => {
    try {
      const response = await api.get('/users/profile');

      return {
        success: true,
        message: 'Profile retrieved successfully',
        data: response.data.data,
      };
    } catch (error: unknown) {
      console.error('Get profile error:', error);
      return {
        success: false,
        message: 'Failed to get profile',
      };
    }
  },

  /**
   * Delete file from S3 by object key
   */
  deleteProfileImage: async (imageKey: string): Promise<{ success: boolean; message: string }> => {
    try {
      await fileService.deleteFile(imageKey);
      return {
        success: true,
        message: 'Image deleted successfully',
      };
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      console.error('Delete profile image error:', error);
      return {
        success: false,
        message: err.response?.data?.message || 'Failed to delete image',
      };
    }
  },
};
